import React from 'react';

import {styled, Switch as MuiSwitch} from '@mui/material';
import { SwitchProps } from '@mui/material/Switch';

/**
 * VERA's own navy-to-purple ON fill, for the BASE theme only.
 *
 * Kept as a literal rather than derived from the ramp, for the reason
 * `CountBadge` records about the identical decision: Vera purple is not a
 * "fallback", it is the base theme's own accent. `apps/platform` is Vera's own
 * console and always renders this.
 */
const VERA_SWITCH_GRADIENT =
  'linear-gradient(122.76deg, #0B0A67 14.25%, #7850B9 84.95%)';

export const Switch = styled((props: SwitchProps) => (
  <MuiSwitch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => {
  /**
   * WHITE-LABEL LEAK, fixed: the ON fill used to paint Vera's gradient on EVERY
   * tenant.
   *
   * The `backgroundColor: brand[600]` below was already a correct brand read —
   * in `apps/app` that ramp is derived from the tenant's own Studio
   * `primary_color` — but the `background-image` on the next line painted over
   * it unconditionally, so the read never reached a pixel and a customer's own
   * storefront showed Vera's purple on its Notifications, Share and gift
   * toggles. Same class of defect as a Vera-branded page background, arriving
   * through a shared component instead.
   *
   * Gated on `palette.brand.isTenantDerived` — the same signal `CountBadge` and
   * `LogoLoader` already branch on (`apps/app/src/settings/mui/brandRamp.ts`) —
   * so this is the split the codebase already makes everywhere else:
   *
   *   apps/platform       static Vera ramp, no such key -> gradient, as today
   *   apps/app, unthemed  `brandRamp(null)` -> false    -> gradient, as today
   *   apps/app, tenant    -> true -> flat `brand[600]`, the brand's own colour
   *
   * `apps/platform` therefore cannot move: nothing there ever sets the flag.
   *
   * NOTE the "5.24:1 in light" figure quoted in the OFF-state note below is the
   * GRADIENT's number and still describes the base theme. A tenant fill spans
   * the whole gamut, so its ON contrast is the brand's to own — exactly as it
   * already is for every other tenant-coloured fill (buttons, `CountBadge`).
   * What signals "a control is here" regardless is the OFF-state border, which
   * is untouched and is what was actually reported as invisible.
   */
  const brandPalette = theme.palette.brand as typeof theme.palette.brand & {
    isTenantDerived?: boolean;
  };
  const tenantDerived = Boolean(brandPalette?.isTenantDerived);

  return {
  width: 42,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(19.5px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.brand[600],
        backgroundImage: tenantDerived ? 'none' : VERA_SWITCH_GRADIENT,
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.brand[500],
          // Only the base theme re-states the gradient. On a tenant the `none`
          // above already holds, so dark falls through to `brand[500]` — the
          // brand's own lighter step, which is the point of the mode split.
          ...(tenantDerived ? {} : { backgroundImage: VERA_SWITCH_GRADIENT }),
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    /**
     * LIGHT OFF fill. `#F0F0F1`, up from `#E9E9EA` (2026-08-31, VRG-491) — see the
     * "how far the fill can go" note under the border below, which is where the two
     * values were chosen together. They cannot be picked independently.
     */
    backgroundColor: '#F0F0F1',
    /**
     * The OFF state's BOUNDARY. Without it the switch is invisible.
     *
     * Both track fills started as MUI's iOS-demo literals, and neither had
     * enough contrast to be found on a Vera page. Measured, off state, AS
     * ORIGINALLY AUTHORED (light is `#F0F0F1` now — see the 2026-08-31 note
     * below; the figure only got smaller, so the argument is unweakened):
     *
     *   light   #e9e9ea on the #f4f5f5 page   1.11:1   (now #F0F0F1, 1.04:1)
     *   dark    #39393d on the #09090b page   1.73:1   (unchanged)
     *
     * against the 3:1 WCAG 1.4.11 asks of a control's boundary. That is not
     * "a bit low" — a pale capsule on a pale page is a control the user cannot
     * see at all, which is what the Monthly/Annual toggle was reported as. The
     * ON state was never the problem (its brand gradient measures 5.24:1 in
     * light), so what actually failed was telling that a switch was THERE.
     *
     * A BORDER rather than a darker fill, for the reason the auth fields
     * settled on the same instrument: a 1px edge does not depend on luminance
     * headroom, so it clears 3:1 on every surface a switch sits on, where a
     * fill would have to be chosen per surface. Darkening the fill instead
     * would also cost the OFF state its "empty" reading: a mid-grey capsule is
     * easily mistaken for ON.
     *
     * ⚠️ CORRECTION (2026-08-31, measured on a live page in both modes). This
     * paragraph used to claim ONE value — `primary[400]` — cleared 3:1
     * everywhere, and quoted "dark page 5.61:1, dark card 4.63:1". Those are
     * `#888888`'s numbers, and `#888888` has never been what dark renders.
     * `primary[400]` is itself mode-dependent: `#888888` in the light theme,
     * `#71717a` (zinc-500) in the dark one. So a single token reference was
     * always TWO rendered values, and the dark half measured:
     *
     *   #71717a   4.12:1 vs the #09090b page   3.96:1 vs a #0f0f12 card
     *
     * Lower than advertised, but still clear of 3:1 — the conclusion survived,
     * the arithmetic did not. Do not re-derive dark's contrast from `#888888`.
     *
     * Not `primary[100]`, the app's usual rest border: that token is calibrated
     * for a field on a WHITE card and measures 1.06:1 against this page. Same
     * trap `authFieldChrome` documents.
     *
     * Checked keeps `border: 0` above — the gradient carries its own contrast,
     * and `box-sizing: border-box` (CssBaseline, inherited) means adding or
     * removing this border does not change the capsule's rendered size.
     *
     * ──────────────────────────────────────────────────────────────────────
     * LIGHTENED 2026-08-31 (VRG-491). The light OFF state read too heavy, so
     * the edge went to the 3:1 floor and the fill came up with it. Both halves
     * are measured, and they are ONE decision — see the trap at the end.
     *
     * `#8d8d8d`, NOT the `primary[400]` token, and NOT `#8e8e8e`. Measured
     * against `background.default` (`#f4f5f5`, confirmed on a live page — NOT
     * `primary[50]` `#f6f6f6`, which is a nav/field token and gives answers ~1%
     * too generous):
     *
     *   #888888   3.245 : 1  vs page   3.545 : 1  vs white card   (was)
     *   #8d8d8d   3.039 : 1  vs page   3.319 : 1  vs white card   (is)
     *   #8e8e8e   2.999 : 1  vs page   3.276 : 1  vs white card   FAILS
     *
     * So `#8d8d8d` is the whole available headroom — one step further is below
     * WCAG 1.4.11's 3:1 for a component boundary. Do not "tidy" this literal
     * onto the nearest palette token: it is a measured floor, and `primary[300]`
     * (`#b0b0b0`, 1.99:1) is not a rounding of it, it is a failure.
     *
     * ⚠️ THE TRAP, and why the fill is capped at `#F0F0F1`. Lightening the fill
     * RAISES the edge's contrast against its own interior, so "lighter fill" and
     * "lighter edge" pull in opposite directions and a naive lightening makes the
     * control read HEAVIER. Edge-vs-fill, holding the edge at `#8d8d8d`:
     *
     *   fill #E9E9EA  2.736      fill #F0F0F1  2.914   <- cap, still under
     *   fill #EDEDEE  2.837      fill #F1F1F2  2.940   <- over baseline
     *   fill #EFEFF0  2.888      fill #F2F2F3  2.967   <- edge reads harder
     *
     * against the 2.922 the old `#888888`/`#E9E9EA` pair measured. `#F0F0F1` is
     * the LIGHTEST fill that still leaves the edge softer against its interior
     * than it was before — past it, you buy a lighter fill by making the ring
     * pop, which is the opposite of the ask. If you lighten the fill again,
     * re-run this number; it is the binding constraint, not the 3:1 floor.
     *
     * The white thumb is the other bound: `#F0F0F1` leaves it at 1.139:1 (from
     * 1.213:1), separated mostly by MUI's `shadows[1]` on the thumb rather than
     * by hue. There is not much left there either.
     *
     * DARK IS DELIBERATELY UNCHANGED, and the split below is how that is kept.
     * Lightening a border on a DARK ground ADDS contrast — the same edit reads
     * HEAVIER there, the opposite of the fix — and dark was never the
     * complaint. So light takes the literal and dark keeps resolving
     * `primary[400]`, which in the dark theme is `#71717a` and measures
     * 4.12:1 / 3.96:1 / 2.38:1 against the dark page / dark card / `#39393D`
     * fill. Verified in the browser after this change: byte-identical to what
     * dark rendered before it.
     *
     * This is LESS of a change than it looks. `primary[400]` already resolved
     * per mode (`#888888` light, `#71717a` dark), so the one-line token
     * reference was always two rendered values. All that is new is that the
     * LIGHT half is now an explicit measured literal instead of a token that
     * happened to sit near the floor. The fill has been mode-split all along.
     */
    border: '1px solid #8d8d8d',
    opacity: 1,
    transition: theme.transitions.create(
      ['background-color', 'border-color'],
      {
        duration: 500,
      }
    ),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
      // Longhand after the shorthand above, inside applyStyles' nested rule, so it
      // wins in dark and leaves light on the measured #8d8d8d.
      borderColor: (theme.palette as any).primary?.[400] || '#888888',
    }),
  },
  };
});
